import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { cn } from '../../utils/cn';

interface EmptyProps extends ComponentPropsWithoutRef<'div'> {
    action?: ReactNode;
    description?: string;
    icon?: ReactNode;
    iconClassName?: string;
    iconContainerClassName?: string;
    maxWidth?: string | number;
    title?: string;
    noBorder?: boolean;
}

/**
 * An empty state component to display when there is no content or data.
 * Displays in a card-like container with optional icon, title, description and action.
 *
 * @param title - The main heading text to display.
 * @param description - Optional secondary text providing more context.
 * @param icon - Optional icon element to display above the title (displayed in a circular background).
 * @param action - Optional action element (e.g., button) to display below the description.
 * @param maxWidth - Custom max-width (CSS value or number in pixels). Defaults to 280px.
 * @param className - Additional CSS classes to merge with default styles.
 *
 * @example
 * <Empty title="No results found" />
 *
 * @example
 * <Empty
 *   icon={<LuSearch />}
 *   title="No results found"
 *   description="We couldn't find any matches for your search. Try adjusting your filters."
 * />
 *
 * @example
 * <Empty
 *   title="No items yet"
 *   description="Get started by adding your first item to the list"
 *   maxWidth={400}
 * />
 *
 */
export const Empty: React.FC<EmptyProps> = ({
    action,
    className,
    description,
    icon,
    iconClassName,
    iconContainerClassName,
    maxWidth = 280,
    noBorder = false,
    title,
    ...props
}) => {
    const descriptionMaxWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center gap-[18px]',
                'bg-white rounded-lg p-6',
                !noBorder && 'border border-[#d2d9e5]',
                className,
            )}
            role="status"
            {...props}
        >
            <div className="flex flex-col items-center gap-2.5">
                {icon && (
                    <div
                        className={cn(
                            'flex items-center justify-center size-[50px] rounded-full bg-surface-light',
                            iconContainerClassName,
                        )}
                    >
                        <div className={cn('text-midnight text-2xl', iconClassName)}>{icon}</div>
                    </div>
                )}
                {title && <h3 className="font-roboto font-bold text-14 text-midnight text-center">{title}</h3>}
                {description && (
                    <p
                        className="font-roboto font-normal text-14 text-midnight text-center"
                        style={{ maxWidth: descriptionMaxWidth }}
                    >
                        {description}
                    </p>
                )}
            </div>
            {action && <div>{action}</div>}
        </div>
    );
};
